import React from 'react';
import { taux } from '../format.js';
import Info from './Info.jsx';

/**
 * La bande du corridor - élément signature du document 05.
 *
 * Un axe, un repère de parité officielle fixe au centre, deux poignées de part
 * et d'autre : le taux aller (EUR vers CFA, à gauche de la parité quand il
 * rapporte) et le taux retour (à droite). La saisie clavier est synchronisée
 * avec les curseurs.
 *
 * L'alerte de spread inversé est visuelle et explicative, jamais bloquante : un
 * taux du mauvais côté de la parité est licite, le moteur calcule une marge
 * négative et l'affiche telle quelle.
 */
export default function Corridor({ officialRate, rateOut, rateIn, onChange, devise }) {
  // Amplitude d'affichage autour de la parité. Assez large pour couvrir les
  // taux réellement pratiqués sans écraser la lecture.
  const min = officialRate - 60;
  const max = officialRate + 60;
  const position = (v) => Math.min(100, Math.max(0, ((v - min) / (max - min)) * 100));

  const sortieInversee = rateOut > officialRate;
  const entreeInversee = rateIn < officialRate;

  return (
    <div>
      <div className="etiquette" style={{ marginBottom: 'var(--espace-2)' }}>
        Corridor - parité officielle {taux(officialRate)} {devise}/EUR
        <Info titre="Parité officielle">
          Le franc CFA est arrimé à l’euro à un taux fixe et légal de 655,957.
          Il ne varie pas. C’est le repère par rapport auquel se mesure votre
          marge de change : appliquer un taux inférieur à l’aller vous fait
          gagner la différence, un taux supérieur vous la fait perdre.
        </Info>
      </div>

      {/* L'axe est décoratif : les valeurs restent saisissables au clavier
          juste en dessous, seule voie réellement accessible. */}
      <div
        aria-hidden="true"
        style={{
          position: 'relative', height: 44, marginBottom: 'var(--espace-3)',
          background: 'var(--couleur-fond)', border: '1px solid var(--couleur-trait)',
          borderRadius: 'var(--rayon)',
        }}
      >
        <div style={{
          position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2,
          background: 'var(--couleur-encre)', opacity: 0.5,
        }} />
        <Poignee
          gauche={position(rateOut)}
          couleur={sortieInversee ? 'var(--couleur-alerte)' : 'var(--couleur-accent)'}
          libelle="aller"
        />
        <Poignee
          gauche={position(rateIn)}
          couleur={entreeInversee ? 'var(--couleur-alerte)' : 'var(--couleur-succes)'}
          libelle="retour"
          bas
        />
      </div>

      <div className="grille-2">
        <Taux
          id="taux-aller"
          etiquette={`Aller - EUR vers ${devise}`}
          valeur={rateOut}
          min={min}
          max={max}
          inverse={sortieInversee}
          couleur="var(--couleur-accent)"
          onChange={(v) => onChange({ rateOut: v })}
        />
        <Taux
          id="taux-retour"
          etiquette={`Retour - ${devise} vers EUR`}
          valeur={rateIn}
          min={min}
          max={max}
          inverse={entreeInversee}
          couleur="var(--couleur-succes)"
          onChange={(v) => onChange({ rateIn: v })}
        />
      </div>

      {(sortieInversee || entreeInversee) && (
        <p
          role="status"
          style={{
            marginTop: 'var(--espace-3)', marginBottom: 0,
            color: 'var(--couleur-alerte)', fontSize: '0.85rem',
          }}
        >
          {sortieInversee && 'À l’aller, le change vous coûte : le taux appliqué dépasse la parité. '}
          {entreeInversee && 'Au retour, le change vous coûte : le taux appliqué est sous la parité. '}
          La marge de change devient négative - c’est licite, le calcul reste juste.
        </p>
      )}
    </div>
  );
}

function Poignee({ gauche, couleur, libelle, bas = false }) {
  return (
    <div
      title={libelle}
      style={{
        position: 'absolute', left: `${gauche}%`, transform: 'translateX(-50%)',
        [bas ? 'bottom' : 'top']: 4, width: 12, height: 16,
        background: couleur, borderRadius: 2,
      }}
    />
  );
}

// La couleur est passée par l'appelant et non déduite ici : c'est la bande, au
// dessus, qui fixe le code - ocre à l'aller, vert au retour. Deux endroits qui
// décideraient chacun de leur côté finiraient par diverger.
function Taux({ id, etiquette, valeur, min, max, inverse, couleur, onChange }) {
  return (
    <div>
      <label htmlFor={id} className="etiquette">{etiquette}</label>
      <input
        id={id}
        type="number"
        step="0.01"
        value={valeur}
        onChange={(e) => onChange(Number(e.target.value))}
        className="champ"
        style={{
          marginTop: 'var(--espace-1)',
          borderColor: inverse ? 'var(--couleur-alerte)' : 'var(--couleur-trait)',
        }}
      />
      {/* Le curseur double la saisie plutôt que de la remplacer : il donne le
          geste rapide, le champ donne la valeur exacte. */}
      <input
        type="range"
        aria-label={`${etiquette}, réglage rapide`}
        min={min}
        max={max}
        step="0.5"
        value={valeur}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          width: '100%', marginTop: 'var(--espace-1)',
          accentColor: inverse ? 'var(--couleur-alerte)' : couleur,
        }}
      />
    </div>
  );
}
